import { useMemo } from 'react';
import { ArrowRightAlt } from '@mui/icons-material';
import { Button, Stack } from '@mui/material';
import { DataGridProps, GridColDef } from '@mui/x-data-grid';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { AdminClaimsQuery } from '../../../../api/gql';
import { InvoiceStateBadge } from '../../../../components/invoice/InvoiceStateBadge';
import { renderCellOrDefault } from '../../../../components/table/renderCellOrDefault';
import { Table } from '../../../../components/table/Table';
import { dateGridColDef } from '../../../../components/table/utils/columnHeader';
import { getInvoiceStateProperties } from '../../../../utils/invoice';

type Props = {
  claims?: AdminClaimsQuery['adminInvoices']['results'];
  transNs?: string;
  keyPrefix?: string;
} & Partial<DataGridProps>;

export const AdminClaimsTable = ({ claims, transNs, keyPrefix, ...rest }: Props) => {
  const { t } = useTranslation(transNs, { keyPrefix });

  const rows = useMemo(
    () =>
      claims?.map((invoice) => ({
        id: invoice.id,
        issueDate: invoice.createdAt,
        number: invoice.number,
        name: invoice.name,
        customer: invoice.customer?.name,
        invoiceId: invoice.id,
        scenario: invoice.scenario?.name,
        owner: `${invoice.owner?.firstName} ${invoice.owner?.lastName}`,
        state: invoice.status,
      })) ?? [],
    [claims],
  );

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'issueDate',
        headerName: t('issueDate') as string,
        minWidth: 150,
        flex: 1,
        ...dateGridColDef,
      },
      {
        field: 'name',
        headerName: t('name') as string,
        flex: 1,
        minWidth: 130,
        renderCell: renderCellOrDefault,
      },
      {
        field: 'number',
        headerName: t('number') as string,
        flex: 1,
        minWidth: 130,
        renderCell: renderCellOrDefault,
      },
      {
        field: 'customer',
        headerName: t('customer') as string,
        flex: 1,
        minWidth: 140,
        renderCell: renderCellOrDefault,
      },
      {
        field: 'owner',
        headerName: t('owner') as string,
        flex: 1,
        minWidth: 140,
        renderCell: renderCellOrDefault,
      },
      {
        field: 'scenario',
        headerName: t('scenario') as string,
        flex: 1,
        minWidth: 140,
        renderCell: renderCellOrDefault,
      },
      {
        field: 'state',
        headerName: t('status') as string,
        flex: 1,
        minWidth: 100,
        valueGetter: (params) => {
          // transform value to enable filtering by its translation;
          if (!params.value) {
            return params.value;
          }
          const transKey = getInvoiceStateProperties(params.value, '')?.transKey;
          return t(transKey);
        },
        renderCell: (params) => {
          return <InvoiceStateBadge state={params.row.state} />;
        },
      },
      {
        field: 'invoiceId',
        headerName: '',
        filterable: false,
        sortable: false,
        disableColumnMenu: true,
        flex: 1,
        align: 'right',
        minWidth: 230,
        renderCell: (params) => (
          <Stack direction={'row'}>
            <Button
              variant={'contained'}
              component={Link}
              to={`/admin/claims/${params.id}`}
              endIcon={<ArrowRightAlt />}
            >
              {t('detail')}
            </Button>
          </Stack>
        ),
      },
    ],
    [t],
  );

  return <Table id={'admin-claims'} {...rest} columns={columns} rows={rows} />;
};
